import trackClevertapEvents from "./trackClevertapEvents";

const isClient = typeof window !== "undefined";

if (isClient) {
  (function () {
    // Function to calculate and display page load time using modern methods
    function calculatePageLoadTime() {
      // Check if Performance API is supported
      if (!window.performance || !performance.getEntriesByType) {
        console.error("Performance API not fully supported in this browser.");
        return null;
      }

      // Get the navigation timing entry
      const navigationEntries = performance.getEntriesByType("navigation");

      if (navigationEntries.length > 0) {
        // Cast to PerformanceNavigationTiming to access the specific properties
        const navigationTiming =
          navigationEntries[0] as PerformanceNavigationTiming;

        // Calculate various timing metrics (in milliseconds)
        const pageLoadTime =
          navigationTiming.loadEventEnd - navigationTiming.startTime;
        const dnsTime =
          navigationTiming.domainLookupEnd - navigationTiming.domainLookupStart;
        const connectionTime =
          navigationTiming.connectEnd - navigationTiming.connectStart;
        const serverResponseTime =
          navigationTiming.responseEnd - navigationTiming.requestStart;
        const domLoadingTime =
          navigationTiming.domComplete -
          navigationTiming.domContentLoadedEventEnd;

        // Log results to console
        trackClevertapEvents.eventPush("PAGE_LOAD_PERFORMANCE_V1", {
          time: pageLoadTime.toFixed(2),
        });
        trackClevertapEvents.eventPush("CONNECTION_TIME_PERFORMANCE_V1", {
          time: pageLoadTime.toFixed(2),
        });

        trackClevertapEvents.eventPush("SERVER_RESPONSE_TIME_PERFORMANCE_V1", {
          time: pageLoadTime.toFixed(2),
        });

        trackClevertapEvents.eventPush("DOM_LOADING_V1", {
          time: pageLoadTime.toFixed(2),
        });

        // Return the data for further use if needed
        return {
          pageLoadTime,
          dnsTime,
          connectionTime,
          serverResponseTime,
          domLoadingTime,
        };
      } else {
        // Fallback for older browsers that don't support the detailed API
        console.warn(
          "Navigation Timing API not available. Using simplified measurement.",
        );
        return useSimpleMeasurement();
      }
    }

    // Simple measurement method as fallback
    function useSimpleMeasurement() {
      const startTime = performance.now();

      return {
        measure: function () {
          const endTime = performance.now();
          const simpleLoadTime = endTime - startTime;
          return simpleLoadTime;
        },
      };
    }

    // Execute when the page has finished loading
    let simpleMeasurement = useSimpleMeasurement();

    // Wait for the page to fully load
    window.addEventListener("load", function () {
      // Small delay to ensure all resources are loaded
      setTimeout(function () {
        // Get detailed metrics
        calculatePageLoadTime();

        // Also log the simple measurement
        simpleMeasurement.measure();
      }, 0);
    });
  })();
}

// Function to get Time to Interactive (TTI)
function calculateTTI(): Promise<number> {
  if (!isClient) {
    return Promise.resolve(0);
  }

  return new Promise((resolve) => {
    let lastLongTaskEnd = performance.now();
    let isNetworkIdle = false;
    let isCPUIdle = false;

    // Track in-flight requests
    let inFlightRequests = 0;

    // Function to check if we've met TTI conditions
    const checkTTIConditions = () => {
      if (isNetworkIdle && isCPUIdle && lastLongTaskEnd > 0) {
        resolve(lastLongTaskEnd);
      }
    };

    // Monitor long tasks
    const longTaskObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        lastLongTaskEnd = entry.startTime + entry.duration;
        isCPUIdle = false;

        // Reset CPU idle check
        setTimeout(() => {
          isCPUIdle = true;
          checkTTIConditions();
        }, 5000); // Wait 5s after last long task
      });
    });

    // Monitor network activity
    const networkObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        // Track start of request
        inFlightRequests++;

        // Track end of request
        const resourceEntry = entry as PerformanceResourceTiming;
        setTimeout(() => {
          inFlightRequests--;
          if (inFlightRequests === 0) {
            isNetworkIdle = true;
            checkTTIConditions();
          }
        }, Math.ceil(resourceEntry.duration));
      });
    });

    // Start observations
    try {
      longTaskObserver.observe({ entryTypes: ["longtask"] });
      networkObserver.observe({ entryTypes: ["resource"] });

      // Set initial CPU idle check
      setTimeout(() => {
        isCPUIdle = true;
        checkTTIConditions();
      }, 5000);

      // Set initial network idle check
      setTimeout(() => {
        isNetworkIdle = true;
        checkTTIConditions();
      }, 5000);
    } catch (error) {
      console.warn("TTI measurement not supported", error);
      resolve(performance.now()); // Fallback to current time
    }

    // Cleanup after 30 seconds to prevent memory leaks
    setTimeout(() => {
      longTaskObserver.disconnect();
      networkObserver.disconnect();
      resolve(lastLongTaskEnd || performance.now());
    }, 30000);
  });
}

function calculateFCP(): Promise<number> {
  return new Promise((resolve) => {
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntriesByName("first-contentful-paint");
      if (entries.length > 0) {
        const fcp = entries[0].startTime;
        resolve(fcp);
      }
    }).observe({ type: "paint", buffered: true });
  });
}

function calculateLCP(): Promise<number> {
  return new Promise((resolve) => {
    let timeoutId: NodeJS.Timeout;
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        resolve(lastEntry.startTime);
      }, 1000); // Delay resolving LCP by 1 second
    }).observe({ type: "largest-contentful-paint", buffered: true });
  });
}

function calculateCLS(): Promise<number> {
  return new Promise((resolve) => {
    let clsScore = 0;
    let clsEntries: PerformanceEntry[] = [];

    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      clsEntries = [...clsEntries, ...entries];
    });

    observer.observe({ type: "layout-shift", buffered: true });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        clsScore = clsEntries.reduce(
          (total, entry) => total + (entry as any).value,
          0,
        );
        observer.disconnect();
        resolve(clsScore);
      }
    });
  });
}

function calculateFID(): Promise<number> {
  if (!isClient) {
    return Promise.resolve(0);
  }

  return new Promise((resolve) => {
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const firstInput = entries[0];
      resolve(
        (firstInput as PerformanceEventTiming).processingStart -
          firstInput.startTime,
      );
    }).observe({ type: "first-input", buffered: true });
  });
}

// Function to calculate Interaction to Next Paint (INP)
function calculateINP(): Promise<number> {
  if (!isClient) {
    return Promise.resolve(0);
  }

  return new Promise((resolve) => {
    // Track all interactions and their latencies
    const interactions: { startTime: number; latency: number; type: string }[] =
      [];
    let maxLatency = 0;
    let hasReported = false;

    // Create callback functions to handle different interaction types
    const onInteraction = (event: MouseEvent | KeyboardEvent | TouchEvent) => {
      const startTime = performance.now();
      const type = event.type;

      // Use requestAnimationFrame to measure when the next frame is painted after the interaction
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // This will execute after the next paint following the interaction
          const latency = performance.now() - startTime;

          // Store this interaction
          interactions.push({
            startTime,
            latency,
            type,
          });

          // Update max latency if this one is higher
          if (latency > maxLatency) {
            maxLatency = latency;
          }
        });
      });
    };

    // Function to report final INP to Clevertap (called only once)
    const reportFinalINP = (inp: number, count: number) => {
      if (hasReported || count === 0) return;

      const time = (inp / 1000).toFixed(3);
      trackClevertapEvents.eventPush("INP_PERFORMANCE_V1", {
        time,
        interactions: count,
        timestamp: new Date().toISOString(),
      });

      hasReported = true;
    };

    // Add event listeners for all interactions we want to track
    const interactionEvents = [
      "click",
      "mousedown",
      "keydown",
      "touchstart",
      "pointerdown",
    ];
    const cleanups: (() => void)[] = [];

    interactionEvents.forEach((eventType) => {
      const handler = (e: Event) => onInteraction(e as any);
      document.addEventListener(eventType, handler, {
        passive: true,
        capture: true,
      });
      cleanups.push(() =>
        document.removeEventListener(eventType, handler, { capture: true }),
      );
    });

    // Set up logging interval (but not reporting to Clevertap)
    // const loggingInterval = setInterval(() => {
    //   if (interactions.length > 0) {
    //     console.log(
    //       `Current max INP: ${maxLatency.toFixed(2)}ms from ${interactions.length} interactions`,
    //     );
    //   }
    // }, 10000);

    // Cleanup function to remove all event listeners
    const cleanup = () => {
      cleanups.forEach((fn) => fn());
      // clearInterval(loggingInterval);
    };

    // Report final value and cleanup on page hide
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden" && interactions.length > 0) {
        reportFinalINP(maxLatency, interactions.length);
        cleanup();
        resolve(maxLatency);
      }
    });

    // Also report final value after 60 seconds
    setTimeout(() => {
      reportFinalINP(maxLatency, interactions.length);
      cleanup();
      resolve(interactions.length > 0 ? maxLatency : 0);
    }, 60000);
  });
}

export default function trackPerformance(): void {
  if (!isClient) {
    return;
  }

  calculateTTI()
    .then((tti) => {
      const time = (tti / 1000).toFixed(3);
      trackClevertapEvents.eventPush("TTI_PERFORMANCE_V1", { time });
    })
    .catch((error) => {
      console.error("Error calculating TTI:", error);
    });

  calculateFCP()
    .then((fcp) => {
      const time = (fcp / 1000).toFixed(3);
      trackClevertapEvents.eventPush("FCP_PERFORMANCE_V1", { time });
    })
    .catch((error) => {
      console.error("Error calculating FCP:", error);
    });

  calculateLCP()
    .then((lcp) => {
      const time = (lcp / 1000).toFixed(3);
      trackClevertapEvents.eventPush("LCP_PERFORMANCE_V1", { time });
    })
    .catch((error) => {
      console.error("Error calculating LCP:", error);
    });

  calculateCLS()
    .then((cls) => {
      trackClevertapEvents.eventPush("CLS_PERFORMANCE_V1", { cls });
    })
    .catch((error) => {
      console.error("Error calculating CLS:", error);
    });

  calculateFID()
    .then((fid) => {
      const time = (fid / 1000).toFixed(3);
      trackClevertapEvents.eventPush("FID_PERFORMANCE_V1", { time });
    })
    .catch((error) => {
      console.error("Error calculating FID:", error);
    });

  // Add INP tracking
  calculateINP()
    .then((inp) => {
      const time = (inp / 1000).toFixed(3);
      // console.log(`INP - Interaction to Next Paint: ${time} seconds`);
    })
    .catch((error) => {
      console.error("Error calculating INP:", error);
    });
}
